import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface EducationData {
  id?: string;
  institution: string;
  degree_or_certification: string;
  status: string;
  display_order: number;
}

interface EducationFormProps {
  initialData?: EducationData;
  onSuccess?: () => void;
}

export const EducationForm = ({ initialData, onSuccess }: EducationFormProps) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    institution: "",
    degree_or_certification: "",
    status: "",
    display_order: 0,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        institution: initialData.institution || "",
        degree_or_certification: initialData.degree_or_certification || "",
        status: initialData.status || "",
        display_order: initialData.display_order || 0,
      });
    }
  }, [initialData]);

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (initialData?.id) {
        // Update existing
        const { error } = await supabase
          .from("education")
          .update(data)
          .eq("id", initialData.id);
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase.from("education").insert([data]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-education"] });
      queryClient.invalidateQueries({ queryKey: ["education"] });
      toast.success(initialData ? "Education updated successfully" : "Education added successfully");

      if (!initialData) {
        setFormData({
          institution: "",
          degree_or_certification: "",
          status: "",
          display_order: 0,
        });
      }

      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="institution">Institution *</Label>
        <Input
          id="institution"
          value={formData.institution}
          onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="degree_or_certification">Degree/Certification *</Label>
        <Input
          id="degree_or_certification"
          value={formData.degree_or_certification}
          onChange={(e) =>
            setFormData({ ...formData, degree_or_certification: e.target.value })
          }
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="status">Status</Label>
        <Input
          id="status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          placeholder="e.g., Completed, In Progress"
        />
      </div>

      <Button type="submit" disabled={mutation.isPending} className="w-full">
        {mutation.isPending ? (initialData ? "Updating..." : "Adding...") : (initialData ? "Update Education" : "Add Education")}
      </Button>
    </form>
  );
};
